import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { User, LoginRequest, SignupRequest, AuthResponse } from "../types/auth";
import { buildInfo } from "../config/buildInfo";
import { apiClient } from "./apiClient";

const STORAGE_KEYS = {
  USER: "luminous_user",
  TOKEN: "luminous_token",
};
const LEGACY_LOGIN_RESPONSE_KEY = "luminous_login_response";

class AuthService {
  private currentUser: User | null = null;
  private token: string | null = null;
  private isHandlingUnauthorized = false;

  constructor() {
    localStorage.removeItem(LEGACY_LOGIN_RESPONSE_KEY);

    // Load user and token from localStorage on initialization
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);

    if (storedUser && storedToken) {
      this.currentUser = JSON.parse(storedUser);
      this.token = storedToken;
    }
  }

  getToken(): string | null {
    return this.token;
  }

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  private getClientPlatform(): "web_app" | "iphone" | "android" {
    const platform = Capacitor.getPlatform();
    if (platform === "ios") {
      return "iphone";
    }
    if (platform === "android") {
      return "android";
    }
    return "web_app";
  }

  private async getClientAppVersion(): Promise<string> {
    if (!Capacitor.isNativePlatform()) {
      return buildInfo.gitHash;
    }

    try {
      const info = await App.getInfo();
      if (info.version && info.build) {
        return `${info.version} (${info.build})`;
      }
      return info.version || info.build || buildInfo.gitHash;
    } catch (error) {
      console.error("Error getting app version:", error);
      return buildInfo.gitHash;
    }
  }

  private persistAuth(user: User, token: string) {
    this.currentUser = user;
    this.token = token;
    this.isHandlingUnauthorized = false;
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  }

  private updateStoredUser(user: User) {
    this.currentUser = user;
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }

  private clearAuth() {
    this.currentUser = null;
    this.token = null;
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(LEGACY_LOGIN_RESPONSE_KEY);
  }

  clearAuthState() {
    this.clearAuth();
  }

  handleUnauthorized(currentPath?: string) {
    if (this.isHandlingUnauthorized) {
      return;
    }

    this.isHandlingUnauthorized = true;
    this.clearAuth();

    const path = currentPath ?? window.location.pathname + window.location.search;
    if (!path.includes("/login")) {
      sessionStorage.setItem("redirectAfterLogin", path);
    }

    window.location.replace("/login?expired=true");
  }

  async login(credentials: LoginRequest): Promise<User> {
    try {
      const data: AuthResponse = await apiClient.fetch("/api/auth/login", {
        method: "POST",
        body: credentials,
      });

      if (!data) {
        console.error("ERROR: No data received");
        throw new Error("No response from server. Please try again.");
      }

      if (!data.user) {
        console.error("ERROR: No user in response");
        console.error("Response data:", JSON.stringify(data, null, 2));
        throw new Error("Invalid response: user data missing");
      }

      if (!data.token) {
        console.error("ERROR: No token in response");
        console.error("Response data:", JSON.stringify(data, null, 2));
        throw new Error("Invalid response: token missing");
      }

      const token = data.token;
      this.persistAuth(data.user, token);

      return data.user;
    } catch (error) {
      console.error("=== LOGIN ERROR ===");
      console.error("Error type:", typeof error);
      console.error("Error:", error);
      if (error instanceof Error) {
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
        throw error;
      }
      console.error("Unknown error type:", error);
      throw new Error("Login failed. Please try again.");
    }
  }

  async continueAsGuest(): Promise<User> {
    try {
      const data: AuthResponse = await apiClient.fetch("/api/auth/guest", {
        method: "POST",
      });

      if (!data.user || !data.token) {
        throw new Error("Invalid guest login response");
      }

      this.persistAuth(data.user, data.token);
      return data.user;
    } catch (error) {
      console.error("Guest login error:", error);
      throw error;
    }
  }

  async signup(userData: SignupRequest): Promise<User> {
    try {
      const data: AuthResponse = await apiClient.fetch("/api/auth/signup", {
        method: "POST",
        body: userData,
      });

      if (!data.user || !data.token) {
        throw new Error("Invalid signup response");
      }

      const token = data.token;
      this.persistAuth(data.user, token);
      return data.user;
    } catch (error) {
      console.error("Signup error:", error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    this.clearAuth();
    this.isHandlingUnauthorized = false;
  }

  async fetchCurrentUser(): Promise<User | null> {
    if (this.currentUser && this.token) {
      return this.currentUser;
    }

    const existingToken = this.token;
    if (!existingToken) {
      return null;
    }

    try {
      const data: AuthResponse = await apiClient.fetch("/api/auth/me");
      if (data && data.user) {
        this.persistAuth(data.user, data.token ?? existingToken);
        return data.user;
      }
      return null;
    } catch (error) {
      console.error("Error getting current user:", error);
      this.clearAuth();
      return null;
    }
  }

  async trackAppOpen(): Promise<User | null> {
    if (!this.token) {
      return this.currentUser;
    }

    const data: AuthResponse = await apiClient.fetch("/api/auth/app-open", {
      method: "POST",
      body: {
        platform: this.getClientPlatform(),
        app_version: await this.getClientAppVersion(),
      },
    });

    if (data?.user) {
      this.updateStoredUser(data.user);
      return data.user;
    }

    return this.currentUser;
  }

  async resetPassword(newPassword: string): Promise<User> {
    try {
      const response = await apiClient.fetch("/api/auth/reset-password", {
        method: "POST",
        body: { new_password: newPassword },
      });

      // Check if we have a proper auth response with user and token
      if (response.user && response.token) {
        this.persistAuth(response.user, response.token);
        return response.user;
      }

      throw new Error(response.message || "Password reset failed");
    } catch (error) {
      console.error("Password reset error:", error);
      throw error;
    }
  }

  getStoredUser(): User | null {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }
}

export const authService = new AuthService();
